import React from "react";
import {
    Box,
    Typography,
    Breadcrumbs,
    Link,
    IconButton,
    Button,
    Stepper,
    Step,
    StepLabel,
    StepButton,
    Chip
} from "@mui/material";

import { styled } from "@mui/material/styles";
import StepConnector from "@mui/material/StepConnector";

import SettingsIcon from "@mui/icons-material/Settings";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import default_theme from "assets/themes/themes";
import styles from "./StepsHeader.module.css";

const NumberStepIcon = ({ icon, active }) => {
    return (
        <Box
            sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: 14,
                background: active
                    ? "linear-gradient(135deg, #35B3AB, #23576B)"
                    : "rgba(35, 87, 107, 0.08)",
                color: active ? "#fff" : "#7898A4",
                transition: "all 0.3s ease",
            }}
        >
            {Number(icon) - 1}
        </Box>
    );
};

const BlueConnector = styled(StepConnector)(() => ({
    "& .MuiStepConnector-line": {
        borderTopWidth: 2,
        borderColor: "rgba(35, 87, 107, 0.12)"
    },
    "&.Mui-active .MuiStepConnector-line": {
        borderColor: "#35B3AB"
    },
    "&.Mui-completed .MuiStepConnector-line": {
        borderColor: "#35B3AB"
    }
}));

const StepsHeader = ({ step = 0, isOverview = false }) => {
    const navigate = useNavigate();
    const { projectId } = useParams();

    const { currentProjectDetail } = useSelector(
        (state) => state.projects
    );

    const authState = useSelector((state) => state.auth);
    const currentUserId = authState?.user?.id;

    if (!currentProjectDetail) return null;

    const isOwner =
        currentProjectDetail.owner_id &&
        String(currentProjectDetail.owner_id) === String(currentUserId);

    const steps = Array.from({ length: step + 1 }, (_, i) => i);

    const STEP_LABELS = [
        "Getting Started",
        "Stakeholder Identification",
        "Stakeholder Engagement",
        "Stakeholder Empowerment"
    ];

    return (
        <Box sx={{ mb: 5 }}>

            {/* Top row */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 4
                }}
            >

                {/* Breadcrumbs */}
                <Breadcrumbs separator={
                    <Box sx={{ color: "rgba(35, 87, 107, 0.25)", mx: 0.5, fontSize: "0.75rem" }}>›</Box>
                }>
                    <Link
                        component="button"
                        underline="none"
                        onClick={() => navigate("/workspaces")}
                        sx={{
                            color: "#7898A4",
                            fontWeight: 500,
                            fontSize: "0.875rem",
                            "&:hover": { color: "#35B3AB" }
                        }}
                    >
                        Projects
                    </Link>

                    {isOverview ? (
                        <Typography
                            sx={{
                                color: "#35B3AB",
                                fontWeight: 600,
                                fontSize: "0.875rem"
                            }}
                        >
                            Project Overview
                        </Typography>
                    ) : (
                        <Link
                            component="button"
                            underline="none"
                            onClick={() =>
                                navigate(`/workspaces/projects/${projectId}`)
                            }
                            sx={{
                                color: "#7898A4",
                                fontWeight: 500,
                                fontSize: "0.875rem",
                                "&:hover": { color: "#35B3AB" }
                            }}
                        >
                            Project Overview
                        </Link>
                    )}

                    {!isOverview &&
                        steps.map((s) =>
                            s === step ? (
                                <Typography
                                    key={s}
                                    sx={{
                                        color: "#35B3AB",
                                        fontWeight: 600,
                                        fontSize: "0.875rem"
                                    }}
                                >
                                    Step {s}
                                </Typography>
                            ) : (
                                <Link
                                    key={s}
                                    component="button"
                                    underline="none"
                                    onClick={() =>
                                        navigate(`/workspaces/projects/${projectId}/step-${s}`)
                                    }
                                    sx={{
                                        color: "#7898A4",
                                        fontWeight: 500,
                                        fontSize: "0.875rem",
                                        "&:hover": { color: "#35B3AB" }
                                    }}
                                >
                                    Step {s}
                                </Link>
                            )
                        )}
                </Breadcrumbs>

                {/* Right side actions */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>

                    {!isOverview && step < STEP_LABELS.length - 1 && (
                        <Button
                            variant="outlined"
                            onClick={() =>
                                navigate(`/workspaces/projects/${projectId}/step-${step + 1}`)
                            }
                            sx={{
                                borderColor: "#35B3AB",
                                color: "#35B3AB",
                                fontWeight: 600,
                                fontSize: "0.8rem",
                                textTransform: "none",
                                borderRadius: "10px",
                                px: 2.5,
                                "&:hover": {
                                    borderColor: "#23576B",
                                    color: "#fff",
                                    backgroundColor: "#23576B"
                                },
                                "&.Mui-disabled": {
                                    borderColor: "rgba(35, 87, 107, 0.15)",
                                    color: "#aab4be",
                                }
                            }}
                        >
                            Next Step →
                        </Button>
                    )}

                    {isOwner && (
                        <IconButton
                            onClick={() =>
                                navigate(`/workspaces/projects/${projectId}/settings`)
                            }
                            sx={{
                                color: "#7898A4",
                                borderRadius: "10px",
                                "&:hover": {
                                    backgroundColor: "rgba(53, 179, 171, 0.08)",
                                    color: "#35B3AB"
                                }
                            }}
                            title="Project Settings"
                        >
                            <SettingsIcon fontSize="small" />
                        </IconButton>
                    )}

                </Box>

            </Box>

            {/* Project Info Card */}
            <Box
                sx={{
                    textAlign: "center",
                    maxWidth: 720,
                    mx: "auto",
                    p: 4,
                    borderRadius: "20px",
                    background: "linear-gradient(135deg, rgba(53, 179, 171, 0.06) 0%, rgba(35, 87, 107, 0.03) 100%)",
                    border: "1px solid rgba(35, 87, 107, 0.08)",
                    mb: 4,
                }}
            >
                {/* Framework Badge */}
                {currentProjectDetail.framework && (
                    <Chip
                        label={currentProjectDetail.framework.name}
                        sx={{
                            mb: 2.5,
                            height: 28,
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            letterSpacing: "0.05em",
                            color: "#35B3AB",
                            background: "rgba(53, 179, 171, 0.08)",
                            border: "1px solid rgba(53, 179, 171, 0.2)",
                            borderRadius: "8px",
                            "& .MuiChip-label": {
                                px: 1.5,
                            },
                        }}
                    />
                )}

                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 700,
                        color: "#23576B",
                        mb: 1.5,
                        lineHeight: 1.3,
                    }}
                >
                    {currentProjectDetail.title}
                </Typography>

                <Typography
                    variant="body1"
                    sx={{
                        color: "#7898A4",
                        lineHeight: 1.7,
                        maxWidth: 560,
                        mx: "auto",
                    }}
                >
                    {currentProjectDetail.description}
                </Typography>
            </Box>

            {/* Step Stepper */}
            {!isOverview && (
                <Box
                    sx={{
                        maxWidth: 900,
                        mx: "auto"
                    }}
                >
                    <Stepper
                        nonLinear
                        activeStep={step}
                        alternativeLabel
                        connector={<BlueConnector />}
                    >
                        {STEP_LABELS.map((label, index) => {
                            const isActive = index === step;
                            const isCompleted = index < step;
                            return (
                                <Step key={label} completed={isCompleted}>
                                    <StepButton
                                        onClick={() =>
                                            navigate(`/workspaces/projects/${projectId}/step-${index}`)
                                        }
                                        sx={{
                                            "& .MuiStepLabel-label": {
                                                color: isActive ? "#23576B" : "#7898A4",
                                                fontWeight: isActive ? 600 : 400,
                                                fontSize: "0.8rem",
                                                mt: 0.5,
                                            },
                                        }}
                                    >
                                        <StepLabel
                                            StepIconComponent={NumberStepIcon}
                                        >
                                            {label}
                                        </StepLabel>
                                    </StepButton>
                                </Step>
                            );
                        })}
                    </Stepper>
                </Box>
            )}

        </Box>
    );
};

export default StepsHeader;
